import { useEffect, useState } from 'react';
import { Bell, BellRing, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/lib/AuthContext';
import api from '@/lib/api';

function urlB64ToUint8Array(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const base = (b64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export const PushOptIn = ({ variant = 'outline', size = 'sm', className = '', fullWidth = false }) => {
  const { user } = useAuth();
  const [supported] = useState(() => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !supported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub && Notification.permission === 'granted');
      } catch (e) { /* noop */ }
    })();
  }, [user, supported]);

  if (!user || !supported) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.error('Activa las notificaciones en los ajustes del navegador'); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get('/push/public-key');
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(data.key) });
      await api.post('/push/subscribe', { subscription: sub.toJSON() });
      await api.post('/push/test').catch(() => {});
      setSubscribed(true);
      toast.success('Alertas de pedidos activadas en este dispositivo');
    } catch (e) {
      toast.error('No se pudieron activar las notificaciones');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post('/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {}); await sub.unsubscribe().catch(() => {}); }
      setSubscribed(false);
      toast('Alertas de pedidos desactivadas');
    } catch (e) { /* noop */ } finally { setBusy(false); }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={`${fullWidth ? 'w-full justify-start' : ''} ${className}`}
      disabled={busy}
      onClick={subscribed ? disable : enable}
      data-testid="push-optin"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : subscribed ? <BellRing className="h-4 w-4 text-brand-orange" /> : <Bell className="h-4 w-4" />}
      {subscribed ? 'Alertas de pedidos activadas' : 'Activar alertas de pedidos'}
    </Button>
  );
};

export default PushOptIn;
